/**
 * Campaign Service - Frontend API
 */

import api from './api';

export interface Campaign {
    _id: string;
    name: string;
    description?: string;
    status: 'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'cancelled' | 'archived';
    type: 'predictive' | 'power' | 'preview' | 'progressive';
    queueId?: {
        _id: string;
        name: string;
    };
    assignedAgentIds: Array<{
        _id: string;
        name: string;
        email: string;
        extension?: string;
    }>;
    dialerSettings: {
        maxConcurrentCalls: number;
        targetAgentWaitTime: number;
        maxDialRatio: number;
        minDialRatio: number;
        maxAbandonRate: number;
        ringTimeout: number;
        retryAttempts: number;
        retryDelayMinutes: number;
    };
    callerIdNumber?: string;
    callerIdName?: string;
    workingHours?: {
        enabled: boolean;
        timezone: string;
        days: string[];
        timeSlots: Array<{ startTime: string; endTime: string }>;
    };
    scheduledStartDate?: string;
    scheduledEndDate?: string;
    startDate?: string;
    endDate?: string;
    statistics?: {
        totalContacts: number;
        contactsDialed: number;
        contactsReached: number;
        totalCalls: number;
        answeredCalls: number;
        abandonedCalls: number;
        totalTalkTime: number;
        avgTalkTime: number;
        avgWaitTime: number;
        currentAnswerRate: number;
        currentDialRatio: number;
    };
    createdAt: string;
    updatedAt?: string;
}

export interface DialerList {
    _id: string;
    name: string;
    description?: string;
    campaignId?: string;
    status: 'active' | 'inactive' | 'completed';
    sourceType: 'csv' | 'manual' | 'api' | 'crm';
    importInfo?: {
        fileName: string;
        importedBy?: string;
        importedAt: string;
        totalRows: number;
        successRows: number;
        failedRows: number;
    };
    statistics: {
        totalContacts: number;
        pendingContacts: number;
        completedContacts: number;
        failedContacts: number;
        dncContacts: number;
    };
    createdAt: string;
}

export interface Agent {
    _id: string;
    name: string;
    email: string;
    extension?: string;
    role: string;
}

export interface CampaignStatus {
    campaign: {
        id: string;
        name: string;
        status: string;
        startDate?: string;
        endDate?: string;
    };
    realtime?: {
        activeCallCount: number;
        availableAgentCount: number;
        currentDialRatio: number;
        answerRate: number;
        abandonRate: number;
        dialCount: number;
        lastDialTime?: string;
    };
    contacts: Record<string, number>;
    assignedAgents: number;
}

const campaignService = {
    // Campaign CRUD
    async getCampaigns(filter?: { status?: string; type?: string }): Promise<Campaign[]> {
        const params = new URLSearchParams();
        if (filter?.status) params.append('status', filter.status);
        if (filter?.type) params.append('type', filter.type);
        
        const response = await api.get(`/campaigns?${params.toString()}`);
        return response.data.data;
    },

    async getCampaign(id: string): Promise<{ campaign: Campaign; lists: DialerList[]; recentStats: any[] }> {
        const response = await api.get(`/campaigns/${id}`);
        return response.data.data;
    },

    async createCampaign(data: Record<string, any>): Promise<Campaign> {
        const response = await api.post('/campaigns', data);
        return response.data.data;
    },

    async updateCampaign(id: string, data: Record<string, any>): Promise<Campaign> {
        const response = await api.put(`/campaigns/${id}`, data);
        return response.data.data;
    },

    async deleteCampaign(id: string): Promise<void> {
        await api.delete(`/campaigns/${id}`);
    },

    async cloneCampaign(id: string, newName: string): Promise<Campaign> {
        const response = await api.post(`/campaigns/${id}/clone`, { newName });
        return response.data.data;
    },

    // Agent assignment
    async getAvailableAgents(): Promise<Agent[]> {
        const response = await api.get('/campaigns/available-agents');
        return response.data.data;
    },

    async assignAgents(campaignId: string, agentIds: string[]): Promise<void> {
        await api.post(`/campaigns/${campaignId}/agents`, { agentIds });
    },

    async removeAgents(campaignId: string, agentIds: string[]): Promise<void> {
        await api.delete(`/campaigns/${campaignId}/agents`, { data: { agentIds } });
    },

    // Campaign control
    async startCampaign(id: string): Promise<void> {
        await api.post(`/campaigns/${id}/start`);
    },

    async pauseCampaign(id: string, reason?: string): Promise<void> {
        await api.post(`/campaigns/${id}/pause`, { reason });
    },

    async resumeCampaign(id: string): Promise<void> {
        await api.post(`/campaigns/${id}/resume`);
    },

    async stopCampaign(id: string, reason?: string): Promise<void> {
        await api.post(`/campaigns/${id}/stop`, { reason });
    },

    async restartCampaign(id: string): Promise<{ resetContacts: number; totalContacts: number }> {
        const response = await api.post(`/campaigns/${id}/restart`);
        return response.data.data;
    },

    // Status
    async getCampaignStatus(id: string): Promise<CampaignStatus> {
        const response = await api.get(`/campaigns/${id}/status`);
        return response.data.data;
    },

    async getActiveCampaignsStatus(): Promise<{
        serviceStatus: { isRunning: boolean; activeCampaigns: number; activeDialerCalls: number; amiConnected: boolean };
        campaigns: CampaignStatus[];
    }> {
        const response = await api.get('/campaigns/active');
        return response.data.data;
    },

    async getCampaignStatistics(id: string): Promise<Campaign['statistics']> {
        const response = await api.get(`/campaigns/${id}/statistics`);
        return response.data.data;
    },

    // Lists
    async addListToCampaign(campaignId: string, listId: string): Promise<void> {
        await api.post(`/campaigns/${campaignId}/lists`, { listId });
    },

    async removeListFromCampaign(campaignId: string, listId: string): Promise<void> {
        await api.delete(`/campaigns/${campaignId}/lists/${listId}`);
    },

    // Dialer service control
    async getDialerStatus(): Promise<{ isRunning: boolean; activeCampaigns: number; activeDialerCalls: number; amiConnected: boolean }> {
        const response = await api.get('/campaigns/dialer/status');
        return response.data.data;
    },

    async startDialerService(): Promise<void> {
        await api.post('/campaigns/dialer/start');
    },

    async stopDialerService(): Promise<void> {
        await api.post('/campaigns/dialer/stop');
    }
};

export default campaignService;
